import React from "react";
import { TableProps } from "@amzn/awsui-components-console";
import { ReportRow, CcTotalRow, InvoiceStatus } from "./types";
import { fmt } from "./utils";

export const STATUS_COLORS: Record<InvoiceStatus, string | undefined> = {
  confirmed: "#e8f5e9", // very subtle green
  "tracker-sourced": "#fff3cd", // yellow
  "amount-mismatch": "#ffe0b2", // orange
  unreconciled: undefined, // no color
};

const STATUS_TITLES: Record<InvoiceStatus, string> = {
  confirmed: "Invoice confirmed in GL",
  "tracker-sourced": "Invoice from Tracker (not in FACD) — verify GL posting",
  "amount-mismatch": "Amount differs between FACD and Tracker — investigate",
  unreconciled: "Unreconciled — awaiting invoice data",
};

function statusCell(r: ReportRow, content: string) {
  const bg = STATUS_COLORS[r.invoiceStatus];
  if (!bg) return <span>{content}</span>;
  return (
    <span
      style={{ backgroundColor: bg, padding: "2px 6px", borderRadius: "3px" }}
      title={STATUS_TITLES[r.invoiceStatus]}
      aria-label={`${content} — ${STATUS_TITLES[r.invoiceStatus]}`}
    >
      {content}
    </span>
  );
}

export const numSortNeg =
  (key: keyof ReportRow) => (a: ReportRow, b: ReportRow) =>
    (b[key] as number) - (a[key] as number);
export const numSort = (key: keyof ReportRow) => (a: ReportRow, b: ReportRow) =>
  (a[key] as number) - (b[key] as number);
export const bold = (_r: ReportRow, content: string) => content;
// Column header prefixes for visual grouping
const CC = "CC: ";
const FC = "FC: ";

export const ST_COLUMNS: TableProps.ColumnDefinition<ReportRow>[] = [
  {
    id: "fixedFeeId",
    header: "Contract",
    cell: (r) => statusCell(r, r.fixedFeeId),
    sortingField: "fixedFeeId",
    minWidth: 200,
  },
  {
    id: "companyCode",
    header: "Company Code",
    cell: (r) => bold(r, r.companyCode),
    sortingField: "companyCode",
  },
  {
    id: "resellerId",
    header: "Reseller ID",
    cell: (r) => r.resellerId,
    sortingField: "resellerId",
  },
  {
    id: "dealStartDate",
    header: "Issuance Date",
    cell: (r) => r.dealStartDate,
    sortingField: "dealStartDate",
  },
  {
    id: "dealEndDate",
    header: "Contract End Date",
    cell: (r) => r.dealEndDate,
    sortingField: "dealEndDate",
  },
  {
    id: "effortType",
    header: "Effort Type",
    cell: (r) => r.effortType,
    sortingField: "effortType",
  },
  {
    id: "contractPrice",
    header: "Total Fee",
    cell: (r) => bold(r, fmt(r.contractPrice)),
    sortingComparator: numSort("contractPrice"),
  },
  {
    id: "currencyCode",
    header: "Contract Currency Code",
    cell: (r) => r.currencyCode,
    sortingField: "currencyCode",
  },
  {
    id: "fxRate",
    header: "Historical FX Rate",
    cell: (r) => r.fxRate.toString(),
  },
  {
    id: "funcCurrency",
    header: "Functional Currency Code",
    cell: (r) => r.funcCurrency,
  },
  // Contract Currency section
  {
    id: "cc_stIssuance",
    header: CC + "ST Issuance",
    cell: (r) => bold(r, fmt(r.stIssuance)),
    sortingComparator: numSort("stIssuance"),
  },
  {
    id: "cc_reclass",
    header: CC + "(+) Reclass from LT",
    cell: (r) => bold(r, fmt(r.cumulativeReclass)),
    sortingComparator: numSort("cumulativeReclass"),
  },
  {
    id: "cc_invoices",
    header: CC + "(-) Invoices Issued",
    cell: (r) => bold(r, fmt(-r.invoicesIssued)),
    sortingComparator: numSortNeg("invoicesIssued"),
  },
  {
    id: "cc_writeoffs",
    header: CC + "(-) Write-offs",
    cell: (r) => bold(r, "0.00"),
  },
  {
    id: "cc_ending",
    header: CC + "(=) Ending Balance",
    cell: (r) => bold(r, fmt(r.stEnding)),
    sortingComparator: numSort("stEnding"),
  },
  // Functional Currency section
  {
    id: "fc_stIssuance",
    header: FC + "ST Issuance",
    cell: (r) => bold(r, fmt(r.funcStIssuance)),
  },
  {
    id: "fc_reclass",
    header: FC + "(+) Reclass from LT",
    cell: (r) => bold(r, fmt(r.funcStReclass)),
  },
  {
    id: "fc_invoices",
    header: FC + "(-) Invoices Issued",
    cell: (r) => bold(r, fmt(-r.functionalInvoicesIssued)),
  },
  {
    id: "fc_writeoffs",
    header: FC + "(-) Write-offs",
    cell: (r) => bold(r, "0.00"),
  },
  {
    id: "fc_fxReval",
    header: FC + "(+/-) FX Revaluation",
    cell: (r) => bold(r, fmt(r.stFxReval)),
  },
  {
    id: "fc_ending",
    header: FC + "(=) Ending Balance",
    cell: (r) => bold(r, fmt(r.funcStEnding)),
  },
];

export const LT_COLUMNS: TableProps.ColumnDefinition<ReportRow>[] = [
  {
    id: "fixedFeeId",
    header: "Contract",
    cell: (r) => statusCell(r, r.fixedFeeId),
    sortingField: "fixedFeeId",
    minWidth: 200,
  },
  {
    id: "companyCode",
    header: "Company Code",
    cell: (r) => bold(r, r.companyCode),
    sortingField: "companyCode",
  },
  {
    id: "resellerId",
    header: "Reseller ID",
    cell: (r) => r.resellerId,
    sortingField: "resellerId",
  },
  {
    id: "dealStartDate",
    header: "Issuance Date",
    cell: (r) => r.dealStartDate,
    sortingField: "dealStartDate",
  },
  {
    id: "dealEndDate",
    header: "Contract End Date",
    cell: (r) => r.dealEndDate,
    sortingField: "dealEndDate",
  },
  {
    id: "effortType",
    header: "Effort Type",
    cell: (r) => r.effortType,
    sortingField: "effortType",
  },
  {
    id: "contractPrice",
    header: "Total Fee",
    cell: (r) => bold(r, fmt(r.contractPrice)),
    sortingComparator: numSort("contractPrice"),
  },
  {
    id: "currencyCode",
    header: "Contract Currency Code",
    cell: (r) => r.currencyCode,
    sortingField: "currencyCode",
  },
  {
    id: "fxRate",
    header: "Historical FX Rate",
    cell: (r) => r.fxRate.toString(),
  },
  {
    id: "funcCurrency",
    header: "Functional Currency Code",
    cell: (r) => r.funcCurrency,
  },
  // Contract Currency section
  {
    id: "cc_ltIssuance",
    header: CC + "LT Issuance",
    cell: (r) => bold(r, fmt(r.ltIssuance)),
    sortingComparator: numSort("ltIssuance"),
  },
  {
    id: "cc_reclass",
    header: CC + "(-) Reclass to ST",
    cell: (r) => bold(r, fmt(-r.cumulativeReclass)),
    sortingComparator: numSortNeg("cumulativeReclass"),
  },
  {
    id: "cc_writeoffs",
    header: CC + "(-) Write-offs",
    cell: (r) => bold(r, "0.00"),
  },
  {
    id: "cc_ending",
    header: CC + "(=) Ending Balance",
    cell: (r) => bold(r, fmt(r.ltEnding)),
    sortingComparator: numSort("ltEnding"),
  },
  // Functional Currency section
  {
    id: "fc_ltIssuance",
    header: FC + "LT Issuance",
    cell: (r) => bold(r, fmt(r.funcLtIssuance)),
  },
  {
    id: "fc_reclass",
    header: FC + "(-) Reclass to ST",
    cell: (r) => bold(r, fmt(-r.funcLtReclass)),
  },
  {
    id: "fc_writeoffs",
    header: FC + "(-) Write-offs",
    cell: (r) => bold(r, "0.00"),
  },
  {
    id: "fc_fxReval",
    header: FC + "(+/-) FX Revaluation",
    cell: (r) => bold(r, fmt(r.ltFxReval)),
  },
  {
    id: "fc_ending",
    header: FC + "(=) Ending Balance",
    cell: (r) => bold(r, fmt(r.funcLtEnding)),
  },
];

export const CC_SUBTOTAL_COLUMNS: TableProps.ColumnDefinition<CcTotalRow>[] = [
  {
    id: "companyCode",
    header: "Company Code",
    cell: (r) => <strong>{r.companyCode}</strong>,
  },
  { id: "configs", header: "Configs", cell: (r) => r.configs },
  {
    id: "fcCurrency",
    header: "Functional Currency",
    cell: (r) => r.fcCurrency,
  },
  {
    id: "fcStEnding",
    header: "ST Ending (GL 13003)",
    cell: (r) => <strong>{fmt(r.fcStEnding)}</strong>,
  },
  {
    id: "fcLtEnding",
    header: "LT Ending (GL 18203)",
    cell: (r) => <strong>{fmt(r.fcLtEnding)}</strong>,
  },
];
